import { Box, Flex, useColorModeValue } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "ctm-compass-react";
import getPaginationPages, { ELLIPSIS } from "./getPaginationPages";
import { useTranslate } from "@tolgee/react";

interface PaginationI {
  current: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
  name?: string;
  canNavigate?: boolean;
  showSummary?: boolean;
  showPages?: boolean;
}

const arrowStyles = {
  w: { base: "40px", md: "24px" },
  h: { base: "40px", md: "24px" },
  borderRadius: { base: "8px", md: "3px" },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  bg: "transparent",
} as const;

function Pagination({
  current,
  pageSize,
  total,
  onChange,
  name = "paginate",
  canNavigate = true,
  showSummary = true,
  showPages = true,
}: PaginationI) {
  const { t } = useTranslate();
  const lastPage = Math.max(1, pageSize ? Math.ceil(total / pageSize) : 1);
  const isFirstPage = current <= 1;
  const isLastPage = current >= lastPage;

  const from = !total ? 0 : current * pageSize - pageSize + 1;
  const to = total ? Math.min(current * pageSize, total) : 0;

  const summaryColor = useColorModeValue(
    "var(--subtleGray)",
    "rgba(255,255,255,0.7)",
  );
  const pageColor = useColorModeValue(
    "var(--compassText)",
    "rgba(255,255,255,0.8)",
  );
  const arrowColor = useColorModeValue(
    "var(--compassText)",
    "rgba(255,255,255,0.8)",
  );
  const hoverBg = useColorModeValue(
    "var(--compassGray2)",
    "rgba(255,255,255,0.08)",
  );
  const pressedBg = useColorModeValue("#d0e2ff", "rgba(255,255,255,0.16)");
  const disabledColor = useColorModeValue(
    "var(--compassGray1)",
    "rgba(255,255,255,0.3)",
  );

  return (
    <Flex
      align="center"
      justify={{ base: "center", md: "flex-end" }}
      userSelect="none"
      color={summaryColor}
      fontSize="16px"
      lineHeight="24px"
    >
      {showSummary && (
        <Box
          display={{ base: "none", md: "block" }}
          mr="10px"
          data-testid={`${name}-summary`}
        >
          {t({
            key: "components.pagination.summary",
            defaultValue: "{from} - {to} of {total}",
            params: { from, to, total },
          })}
        </Box>
      )}

      {canNavigate && (
        <Box
          as="button"
          type="button"
          name={`${name}-previous-button`}
          aria-label={t({
            key: "components.pagination.previousAria",
            defaultValue: "Previous page",
          })}
          aria-disabled={isFirstPage}
          onClick={() => !isFirstPage && onChange(current - 1)}
          color={isFirstPage ? disabledColor : arrowColor}
          cursor={isFirstPage ? "default" : "pointer"}
          pointerEvents={isFirstPage ? "none" : "auto"}
          _hover={isFirstPage ? undefined : { bg: hoverBg }}
          _active={isFirstPage ? undefined : { bg: pressedBg }}
          {...arrowStyles}
        >
          <ChevronLeftIcon />
        </Box>
      )}

      {showPages && (
        <Flex gap="4px">
          {getPaginationPages(current, pageSize, total).map((page, index) => {
            const isEllipsis = page === ELLIPSIS;
            const isActive = page === current;
            return (
              <Box
                key={`${page}-${index}`}
                as="button"
                type="button"
                name={`${name}-page-button`}
                aria-label={
                  isEllipsis
                    ? t({
                        key: "components.pagination.moreAria",
                        defaultValue: "More pages",
                      })
                    : t({
                        key: "components.pagination.pageAria",
                        defaultValue: "Page {pageNumber}",
                        params: { pageNumber: page },
                      })
                }
                aria-current={isActive ? "page" : undefined}
                onClick={() =>
                  !isEllipsis && !isActive && onChange(page as number)
                }
                disabled={isEllipsis}
                cursor={isEllipsis ? "default" : "pointer"}
                pointerEvents={isEllipsis ? "none" : "auto"}
                minW={{ base: "40px", md: "24px" }}
                w={{ base: "40px", md: "unset" }}
                h={{ base: "40px", md: "24px" }}
                px={{ base: 0, md: "6px" }}
                borderRadius={{ base: "8px", md: "3px" }}
                display="flex"
                alignItems="center"
                justifyContent="center"
                border="none"
                fontSize="16px"
                fontWeight={isActive ? 900 : 400}
                bg={isActive ? "var(--primaryColor)" : "transparent"}
                color={isActive ? "#fff" : pageColor}
                _hover={isActive || isEllipsis ? undefined : { bg: hoverBg }}
                _active={isActive || isEllipsis ? undefined : { bg: pressedBg }}
              >
                {page}
              </Box>
            );
          })}
        </Flex>
      )}

      {canNavigate && (
        <Box
          as="button"
          type="button"
          name={`${name}-next-button`}
          aria-label={t({
            key: "components.pagination.nextAria",
            defaultValue: "Next page",
          })}
          aria-disabled={isLastPage}
          onClick={() => !isLastPage && onChange(current + 1)}
          color={isLastPage ? disabledColor : arrowColor}
          cursor={isLastPage ? "default" : "pointer"}
          pointerEvents={isLastPage ? "none" : "auto"}
          _hover={isLastPage ? undefined : { bg: hoverBg }}
          _active={isLastPage ? undefined : { bg: pressedBg }}
          {...arrowStyles}
        >
          <ChevronRightIcon />
        </Box>
      )}
    </Flex>
  );
}

export default Pagination;
